import React, { FC } from 'react'
import { Typography as Text } from '../components/typography/Typography'
import ContainedGrid, {
	GridItem,
} from '../components/contained-grid/ContainedGrid'
import ArticleCard, {
	ArticleCardProps,
} from '../components/article-card/ArticleCard'
import NavigationCard, {
	NavigationCardProps,
} from '../components/navigation-card/NavigationCard'
import Spacing from '../components/spacing/Spacing'
import Container from '../components/container/Container'
import Layout from '../components/layout/Layout'
import { PageContextProps } from '../../common/types/pageContext'
import { graphql } from 'gatsby'
import { DatoImage } from '../types/pages'
import { useUrlUtils } from '../hooks/useUrlUtils'
import { formatDate } from '../utils/formatDate'

interface PagesProps {
	id: string
	title: string
	tileDescription: string
	slug: string
	icon: string
	tileImage: DatoImage | null
}

interface HomepageDataProps {
	id: string
	title: string
	pages: PagesProps[]
}

interface MscArticleNodeProps {
	id: string
	title: string
	images?: DatoImage[]
	slug: string
	meta: { createdAt: string }
	parentPage?: { slug: string }
	localisedParentPage?: { slug: string }
}

interface MscArticleProps {
	edges: { node: MscArticleNodeProps }[]
}

interface IndexProps {
	pageContext: PageContextProps
	data: {
		datoCmsMscHomepage: HomepageDataProps
		allDatoCmsMscArticle: MscArticleProps
	}
}

type ParentPageSlug = {
	slug: string
}

/**
 * Get parent page slug
 * @param parentPage
 * @param localizedParentPage
 */
const getParentPageSlug = (
	parentPage?: ParentPageSlug,
	localizedParentPage?: ParentPageSlug,
) => {
	if (parentPage) {
		return parentPage.slug
	} else if (localizedParentPage) {
		return localizedParentPage.slug
	}
	return false
}

const Index: FC<IndexProps> = ({ pageContext, data }) => {
	const { parseUrl } = useUrlUtils()
	const { locale, dateFormat } = pageContext
	const { title, pages } = data?.datoCmsMscHomepage

	return (
		<Layout>
			<Container hasOffset>
				<Spacing height={[56, 80]} />
				<Text variant="pageHeading" accent="navyBlue">
					{title}
				</Text>
				<Spacing height={[40, 56]} />
			</Container>
			<ContainedGrid hasOffset>
				{data?.allDatoCmsMscArticle.edges.map((edge) => {
					const {
						id,
						title,
						images,
						slug,
						meta,
						parentPage,
						localisedParentPage,
					} = edge.node

					const hasImage = images && images.length

					const date = formatDate(new Date(meta.createdAt), dateFormat, locale)
					const parentPageSlug = getParentPageSlug(
						parentPage,
						localisedParentPage,
					)
					// This is an edge case if the content editors are not secure
					// Either the parent page or the localized parent page should exist
					if (!parentPageSlug) {
						return null
					}

					const articleData: ArticleCardProps = {
						subHeading: title,
						href: parseUrl(`/${parentPageSlug}/${slug}`),
						imageProps: {
							src: (hasImage && images[0].url) || undefined,
							alt: (hasImage && images[0].alt) || undefined,
							aspectRatio: [2, 1],
						},
						date,
					}

					return (
						<GridItem span={[12, 6, 4, 4, 4]} key={id}>
							<ArticleCard {...articleData} />
						</GridItem>
					)
				})}
			</ContainedGrid>
			<Spacing height={[56, 80]} />

			<Container hasOffset withPageWrapper>
				<Spacing height={[56, 80]} />
			</Container>
			<ContainedGrid hasOffset withPageWrapper>
				{pages.map((page) => {
					const navigationCardData: NavigationCardProps = {
						title: page.title,
						description: page.tileDescription,
						imageProps: {
							src: page.tileImage?.url,
							alt: page.tileImage?.alt,
							aspectRatio: [2, 1],
						},
						href: parseUrl(`/${page.slug}`),
						icon: (page?.icon as NavigationCardProps['icon']) || 'boat',
					}

					return (
						<GridItem span={[12, 6, 4, 4, 4]} key={page.id}>
							<NavigationCard {...navigationCardData} />
						</GridItem>
					)
				})}
			</ContainedGrid>
			<Container hasOffset withPageWrapper>
				<Spacing height={[80, 100]} />
			</Container>
		</Layout>
	)
}
export default Index

export const query = graphql`
	query homepage($locale: String) {
		datoCmsMscHomepage(locale: { eq: $locale }) {
			id
			title
			pages {
				... on DatoCmsMscArticleOverview {
					id
					title
					tileDescription
					slug
					icon
					tileImage {
						alt
						url(imgixParams: { w: "760" })
						width
						height
					}
				}
				... on DatoCmsMscEmbedPage {
					id
					title
					tileDescription
					slug
					icon
					tileImage {
						alt
						url(imgixParams: { w: "760" })
						width
						height
					}
				}
				... on DatoCmsMscOverviewPage {
					id
					title
					tileDescription
					slug
					icon
					tileImage {
						alt
						url(imgixParams: { w: "760" })
						width
						height
					}
				}
				... on DatoCmsMscContactOverview {
					id
					title
					tileDescription
					slug
					icon
					tileImage {
						alt
						url(imgixParams: { w: "760" })
						width
						height
					}
				}
			}
		}
		allDatoCmsMscArticle(
			sort: { meta: { createdAt: DESC } }
			limit: 3
			filter: {
				locale: { eq: $locale }
				title: { regex: "/.+/" }
				localizedParentPage: { slug: { ne: "extra-news" } }
				parentPage: { slug: { ne: "extra-news" } }
			}
		) {
			edges {
				node {
					id
					title
					slug
					images {
						alt
						url(imgixParams: { w: "760" })
						width
						height
					}
					meta {
						createdAt
					}
					parentPage {
						slug
					}
					localizedParentPage {
						slug
					}
				}
			}
		}
		allDatoCmsMscDictionary(filter: { locale: { eq: $locale } }) {
			edges {
				node {
					key
					value
				}
			}
		}
	}
`
